/**
 * Updates the product gallery when a cushion color is selected. Native Self
 * Shelf bundles also use the selected cushion color as the product variant.
 */
class CushionAddonSelector extends HTMLElement {
  #abortController = null;
  #baseGalleryHtml = null;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);

    const section = this.closest('.shopify-section');
    const gallery = section?.querySelector('media-gallery');
    if (gallery) this.#baseGalleryHtml = gallery.outerHTML;

    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput instanceof HTMLInputElement) {
      this.#syncCombinedPrice(checkedInput);
      this.#syncNativeBundleVariant(checkedInput);
      this.#updateGalleryForInput(checkedInput, true);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#abortController?.abort();
  }

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    this.#syncCombinedPrice(input);
    this.#syncNativeBundleVariant(input);
    this.#updateGalleryForInput(input);
  };

  #syncCombinedPrice(input) {
    const productId = this.dataset.combinedPriceProductId;
    const formattedPrice = input.dataset.combinedPrice;
    if (!productId || !formattedPrice) return;

    const section = this.closest('.shopify-section');
    const targets = section?.querySelectorAll(
      `product-price[data-product-id="${CSS.escape(productId)}"], sticky-add-to-cart[data-product-id="${CSS.escape(productId)}"]`
    );

    for (const target of targets || []) {
      const container = target.querySelector('[ref="priceContainer"]');
      const price = container?.querySelector('.price');
      if (!price) continue;

      if (target.matches('product-price')) {
        container.setAttribute('aria-live', 'polite');
        container.setAttribute('aria-atomic', 'true');
      }

      const labels = [...price.children].filter((child) => child.classList.contains('visually-hidden'));
      price.replaceChildren(...labels, document.createTextNode(formattedPrice));
    }
  }

  /** @param {HTMLInputElement} input */
  #syncNativeBundleVariant(input) {
    if (this.dataset.nativeBundle !== 'true' || !input.value) return;

    const productDetails = this.closest('.product-details');
    const productForm = productDetails?.querySelector('product-form-component');
    const variantInput = productForm?.querySelector('input[name="id"]');

    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = input.value;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', input.value);
    history.replaceState({}, '', url.toString());
  }

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} [initial]
   */
  #updateGalleryForInput(input, initial = false) {
    const previewVariantId = input.dataset.previewVariantId;
    const showDefaultGallery = input.hasAttribute('data-preview-default');
    if (!previewVariantId && !showDefaultGallery) return;

    const galleryImageUrls = input.dataset.previewGalleryImages?.split(',').filter(Boolean) || [];
    const galleryMediaIds = input.dataset.previewGalleryMediaIds?.split(',').filter(Boolean) || [];
    const productTitle = this.dataset.productTitle || '';
    const previewOption = input.dataset.previewOption || '';

    this.#showVariantMedia(
      previewVariantId || null,
      input.dataset.previewImageUrl || null,
      input.dataset.previewMediaId || null,
      galleryMediaIds,
      galleryImageUrls,
      productTitle,
      previewOption,
      initial
    );
  }

  /**
   * @param {string | null} variantId
   * @param {string | null} previewImageUrl
   * @param {string | null} previewMediaId
   * @param {string[]} galleryMediaIds
   * @param {string[]} galleryImageUrls
   * @param {string} productTitle
   * @param {string} previewOption
   * @param {boolean} initial
   */
  async #showVariantMedia(
    variantId,
    previewImageUrl,
    previewMediaId,
    galleryMediaIds,
    galleryImageUrls,
    productTitle,
    previewOption,
    initial
  ) {
    const productUrl = this.dataset.productUrl;
    const sectionId = this.dataset.sectionId;
    const section = this.closest('.shopify-section');
    const currentGallery = section?.querySelector('media-gallery');

    if (!currentGallery) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.setAttribute('aria-busy', 'true');
    currentGallery.setAttribute('aria-busy', 'true');

    try {
      // Keep the server-rendered LCP image in place on first load. Subsequent
      // color changes still start from the full cached gallery, including video.
      let newGallery = initial && currentGallery.dataset.initialPreview ? currentGallery : this.#galleryFromCache();
      if (!newGallery) {
        if (!productUrl || !sectionId) return;

        const url = new URL(productUrl, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);
        else url.searchParams.delete('variant');
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url, { signal: this.#abortController.signal });
        if (!response.ok) throw new Error(`Gallery request failed with status ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        newGallery = html.querySelector('media-gallery');
      }

      if (!newGallery) throw new Error('The refreshed product gallery was not found');

      if (galleryMediaIds.length) this.#ensureGalleryMedia(newGallery, galleryMediaIds);

      if (previewImageUrl && previewMediaId) {
        this.#replacePreviewImage(
          newGallery,
          previewMediaId,
          previewImageUrl,
          this.#previewAlt(productTitle, previewOption, previewImageUrl)
        );
      }

      if (galleryMediaIds.length) {
        galleryMediaIds.forEach((mediaId, index) => {
          const imageUrl = galleryImageUrls[index];
          if (imageUrl) {
            this.#replacePreviewImage(
              newGallery,
              mediaId,
              imageUrl,
              this.#previewAlt(productTitle, previewOption, imageUrl)
            );
          }
        });
        this.#limitGallery(newGallery, galleryMediaIds);
      }

      if (newGallery !== currentGallery) {
        currentGallery.replaceWith(newGallery);
      } else {
        // Initial filtering can run before Component's idle mutation observer.
        for (const component of newGallery.querySelectorAll('slideshow-component, zoom-dialog')) {
          component.updatedCallback?.();
        }
        newGallery.updatedCallback?.();
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cushion-addon] Unable to update product gallery:', error);
    } finally {
      this.removeAttribute('aria-busy');
      currentGallery.removeAttribute('aria-busy');
    }
  }

  #galleryFromCache() {
    if (!this.#baseGalleryHtml) return null;

    const html = new DOMParser().parseFromString(this.#baseGalleryHtml, 'text/html');
    return html.querySelector('media-gallery');
  }

  /**
   * The initial product markup can omit featured media assigned to another
   * variant. Clone a non-selected gallery item so every requested slot exists
   * before its image is replaced and the gallery is filtered.
   * @param {Element} gallery
   * @param {string[]} mediaIds
   */
  #ensureGalleryMedia(gallery, mediaIds) {
    const keepIds = new Set(mediaIds);
    const groups = [
      gallery.querySelector('slideshow-slides'),
      gallery.querySelector('.media-gallery__grid'),
      gallery.querySelector('.dialog-zoomed-gallery'),
    ].filter(Boolean);

    for (const group of groups) {
      const items = [...group.children].filter((item) => item.querySelector('[data-media-id]'));
      const presentIds = new Set(items.map((item) => item.querySelector('[data-media-id]')?.dataset.mediaId));
      const template =
        items.find((item) => !keepIds.has(item.querySelector('[data-media-id]')?.dataset.mediaId || '')) || items[0];

      if (!template) continue;

      for (const mediaId of mediaIds) {
        if (presentIds.has(mediaId)) continue;

        const clone = template.cloneNode(true);
        const mediaNodes = [clone, ...clone.querySelectorAll('[data-media-id]')];
        for (const node of mediaNodes) {
          if (node instanceof HTMLElement && node.hasAttribute('data-media-id')) node.dataset.mediaId = mediaId;
        }
        for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
        group.append(clone);
        presentIds.add(mediaId);
      }
    }
  }

  /**
   * Replaces every rendered copy of the selected media, including gallery,
   * slideshow controls, and zoom content.
   * @param {Element} gallery
   * @param {string} mediaId
   * @param {string} imageUrl
   * @param {string} altText
   */
  #replacePreviewImage(gallery, mediaId, imageUrl, altText) {
    const mediaImages = gallery.querySelectorAll(`[data-media-id="${mediaId}"] img`);
    for (const image of mediaImages) {
      const sizes = image.getAttribute('sizes') || '(min-width: 750px) 50vw, 100vw';
      const responsiveWidths = [320, 480, 640, 832, 1100];

      // The approved initial gallery is already responsive in the HTML. Do not
      // reset its image candidates (or decoding) while the first screen paints.
      const previewSrc = this.#imageUrlAtWidth(imageUrl, 832);
      if (image.src !== previewSrc || !image.getAttribute('srcset')) {
        image.setAttribute('src', previewSrc);
        image.setAttribute(
          'srcset',
          responsiveWidths.map((width) => `${this.#imageUrlAtWidth(imageUrl, width)} ${width}w`).join(', ')
        );
      }
      image.setAttribute('sizes', sizes);
      image.setAttribute('data_max_resolution', imageUrl);
      if (altText) image.setAttribute('alt', altText);
    }
  }

  /**
   * Shopify's image CDN resizes theme assets through the width query parameter.
   * Keeping the original version parameter lets the CDN cache-bust correctly.
   * @param {string} imageUrl
   * @param {number} width
   */
  #imageUrlAtWidth(imageUrl, width) {
    const url = new URL(imageUrl, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  }

  /**
   * Builds a useful, color-accurate label for each generated gallery view.
   * @param {string} productTitle
   * @param {string} previewOption
   * @param {string} imageUrl
   */
  #previewAlt(productTitle, previewOption, imageUrl) {
    const filename = imageUrl.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
    let view = 'front view';

    if (filename.includes('fold')) view = 'folded view';
    else if (filename.includes('under-2')) view = 'underside detail';
    else if (filename.includes('under')) view = 'underside view';
    else if (filename.includes('right')) view = 'right side view';
    else if (filename.includes('left')) view = 'left side view';
    else if (filename.includes('rear')) view = 'rear view';
    else if (filename.includes('overhead')) view = 'overhead view';
    else if (filename.includes('detail')) view = 'detail view';
    else if (filename.includes('angle')) view = 'angled view';

    const optionPhrase =
      previewOption.toLowerCase() === 'no cushion'
        ? 'without a cushion'
        : previewOption
          ? `with ${previewOption} cushion`
          : '';

    return [productTitle, optionPhrase, view].filter(Boolean).join(', ');
  }

  /**
   * Keeps only the media assigned to the selected cushion and reindexes the
   * gallery controls before the new custom elements connect.
   * @param {Element} gallery
   * @param {string[]} mediaIds
   */
  #limitGallery(gallery, mediaIds) {
    const keepIds = new Set(mediaIds);
    const orderedGroups = [
      gallery.querySelector('slideshow-slides'),
      gallery.querySelector('.media-gallery__grid'),
      gallery.querySelector('.dialog-zoomed-gallery'),
    ].filter(Boolean);

    for (const group of orderedGroups) {
      const items = [...group.children].filter((item) => item.querySelector('[data-media-id]'));

      for (const item of items) {
        const mediaId = item.querySelector('[data-media-id]')?.dataset.mediaId;
        if (!mediaId || !keepIds.has(mediaId)) item.remove();
      }

      const order = new Map(mediaIds.map((mediaId, index) => [mediaId, index]));
      const keptItems = [...group.children].filter((item) => item.querySelector('[data-media-id]'));
      keptItems.sort((first, second) => {
        const firstId = first.querySelector('[data-media-id]')?.dataset.mediaId;
        const secondId = second.querySelector('[data-media-id]')?.dataset.mediaId;
        return (order.get(firstId) ?? Number.MAX_SAFE_INTEGER) - (order.get(secondId) ?? Number.MAX_SAFE_INTEGER);
      });
      keptItems.forEach((item, index) => {
        // Do not detach/reinsert the already-correct first image during hydration.
        if (group.children[index] !== item) group.insertBefore(item, group.children[index] || null);
      });
    }

    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.setAttribute('on:click', slide.getAttribute('on:click')?.replace(/\/open\/\d+/, `/open/${index}`) || '');
      slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
    });

    const gridItems = [...gallery.querySelectorAll('.media-gallery__grid > li')];
    gridItems.forEach((item, index) => {
      const zoomButton = item.querySelector('[on\\:click*="/zoom/"]');
      if (zoomButton) {
        zoomButton.setAttribute('on:click', `/zoom/${index}`);
        zoomButton.setAttribute('aria-label', `Zoom media ${index + 1}`);
      }
    });

    const dotItems = [...gallery.querySelectorAll('.slideshow-controls__dots > li')];
    dotItems.forEach((item, index) => {
      if (index >= mediaIds.length) {
        item.remove();
        return;
      }

      const button = item.querySelector('button');
      if (!button) return;
      button.textContent = String(index + 1);
      button.setAttribute('aria-label', `Slide ${index + 1} of ${mediaIds.length}`);
      button.setAttribute('on:click', `/select/${index}`);
      button.style.setProperty('animation-timeline', `--slide-${index}`);
      button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
    });

    const thumbnails = gallery.querySelector('[ref="thumbnails"]');
    const thumbnailTemplate = thumbnails?.querySelector('button');
    const zoomItems = [...gallery.querySelectorAll('.dialog-zoomed-gallery > li')];
    if (thumbnails && thumbnailTemplate) {
      thumbnails.replaceChildren(...zoomItems.map((item, index) => {
        const button = thumbnailTemplate.cloneNode(true);
        const image = item.querySelector('img');
        button.setAttribute('on:click', `/handleThumbnailClick/${index}`);
        button.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
        button.setAttribute('aria-label', `Zoom media ${index + 1}`);
        button.removeAttribute('aria-selected');
        button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        if (image) {
          const thumbnail = image.cloneNode(true);
          thumbnail.setAttribute('loading', 'lazy');
          thumbnail.setAttribute('fetchpriority', 'low');
          thumbnail.setAttribute('sizes', '160px');
          button.replaceChildren(thumbnail);
        }
        return button;
      }));
    }

    const slideshow = gallery.querySelector('slideshow-component');
    if (slideshow) {
      slideshow.setAttribute('initial-slide', '0');
      slideshow.style.setProperty('--slideshow-timeline', mediaIds.map((_, index) => `--slide-${index}`).join(', '));
      if (mediaIds.length > 1) slideshow.setAttribute('infinite', '');
      else slideshow.removeAttribute('infinite');
    }
  }
}

if (!customElements.get('cushion-addon-selector')) {
  customElements.define('cushion-addon-selector', CushionAddonSelector);
}
